import { collection, query, where, getDocs, getDoc, doc, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/firebase';

/**
 * User Recommendation Access Service
 * Handles fetching user recommendation access data from Firestore
 */
class UserRecommendationAccessService {
  static COLLECTION_NAME = 'userRecommendationAccess';
  static RECOMMENDATIONS_COLLECTION = 'reportRecommendations';

  /**
   * Get all active access records for a user
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Array of access records
   */
  static async getUserAccessRecords(userId) {
    try {
      console.log('🔍 Fetching access records for user:', userId);
      
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('userId', '==', userId),
        where('isActive', '==', true),
        where('isDeleted', '==', false),
        orderBy('grantedAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const records = snapshot.docs.map(doc => {
        const data = doc.data();
        return { 
          id: doc.id, 
          ...data,
          grantedAt: data.grantedAt
        };
      });
      
      console.log(`✅ Found ${records.length} access records for user ${userId}`);
      return records;
    } catch (error) {
      console.error('❌ Error fetching user access records:', error);
      throw error;
    }
  }

  /**
   * Get recommendation by ID
   * @param {string} recommendationId - Recommendation ID
   * @returns {Promise<Object|null>} Recommendation object or null
   */
  static async getRecommendation(recommendationId) {
    try {
      const docRef = doc(db, this.RECOMMENDATIONS_COLLECTION, recommendationId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return { 
          id: docSnap.id, 
          ...data,
          broadcastDate: data.broadcastDate
        };
      }
      
      console.warn(`⚠️ Recommendation not found: ${recommendationId}`);
      return null;
    } catch (error) {
      console.error('❌ Error fetching recommendation:', error);
      throw error;
    }
  }

  /**
   * Get user's accessible recommendations
   * Merges access records with recommendation details
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Array of recommendations with access info
   */
  static async getUserAccessibleRecommendations(userId) {
    try {
      console.log('🔍 Fetching accessible recommendations for user:', userId);
      
      // Get access records
      const accessRecords = await this.getUserAccessRecords(userId);
      
      if (!accessRecords || accessRecords.length === 0) {
        console.log('ℹ️ No access records found for user');
        return [];
      }

      // Fetch recommendation details for each access record
      const recommendations = [];
      
      for (const access of accessRecords) {
        try {
          const recommendation = await this.getRecommendation(access.recommendationId);
          
          if (recommendation) {
            recommendations.push({
              ...recommendation,
              accessType: access.accessType,
              grantedAt: access.grantedAt,
              subscriptionPlanId: access.subscriptionPlanId,
              accessId: access.id
            });
          }
        } catch (err) {
          console.error(`❌ Error fetching recommendation ${access.recommendationId}:`, err);
          // Continue with other recommendations
        }
      }

      console.log(`✅ Found ${recommendations.length} accessible recommendations for user`);
      return recommendations;
    } catch (error) {
      console.error('❌ Error fetching user accessible recommendations:', error);
      throw error;
    }
  }
}

export default UserRecommendationAccessService;
